import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { ShaderPass } from "three/examples/jsm/postprocessing/ShaderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";

/**
 * Киношный свет поверх сцены (Джон 04.11.2026, 23:16: «освещение киношное… чтобы не было так плоско»): свечение только
 * ярких мест (раскалённый металл, искры, магия), грейдинг «тёплые света, холодные тени» и лёгкая виньетка. Бюджет кадра
 * (GAME-17): если кадр сцены дольше FRAME_BUDGET_MS в среднем — свечение выключается ступенью вниз; 60 к/с важнее.
 */
const FRAME_BUDGET_MS = 9;
const SAMPLE_FRAMES = 90;
const CAPTURE = typeof location !== "undefined" && new URLSearchParams(location.search).get("capture") === "1";

const GradeShader = {
  uniforms: { tDiffuse: { value: null }, vignette: { value: 0.32 } },
  vertexShader: `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float vignette;
    varying vec2 vUv;
    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      float light = dot(color.rgb, vec3(0.2126, 0.7152, 0.0722));
      // тени чуть холоднее, света чуть теплее — без потери яркости бойцов
      vec3 cold = vec3(0.92, 0.97, 1.08);
      vec3 warm = vec3(1.06, 1.0, 0.92);
      color.rgb *= mix(cold, warm, smoothstep(0.08, 0.6, light));
      vec2 centered = vUv - 0.5;
      color.rgb *= 1.0 - vignette * smoothstep(0.35, 0.85, length(centered * vec2(1.25, 1.0)));
      gl_FragColor = color;
    }`,
};

export class PostFx {
  private readonly composer: EffectComposer;
  private readonly bloom: UnrealBloomPass;
  private times: number[] = [];
  private stepped = false;

  constructor(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, width: number, height: number) {
    this.composer = new EffectComposer(renderer);
    this.composer.setSize(width, height);
    this.composer.addPass(new RenderPass(scene, camera));
    // свечение в четверть кадра (дешевле; размыто оно и так), порог высокий: светятся только металл, искры и магия, а не тела бойцов
    this.bloom = new UnrealBloomPass(new THREE.Vector2(width / 4, height / 4), 0.6, 0.55, 0.82);
    this.composer.addPass(this.bloom);
    this.composer.addPass(new ShaderPass(GradeShader));
    this.composer.addPass(new OutputPass());
  }

  render(): void {
    const start = performance.now();
    this.composer.render();
    // в съёмке кадр шагает скрипт, а не экран: замер времени там ничего не значит — свет полный
    if (this.stepped || CAPTURE) return;
    this.times.push(performance.now() - start);
    if (this.times.length < SAMPLE_FRAMES) return;
    const average = this.times.reduce((sum, time) => sum + time, 0) / this.times.length;
    this.times = [];
    if (average > FRAME_BUDGET_MS) {
      this.bloom.enabled = false;
      this.stepped = true;
    }
  }
}
